/**
 * meetingChunkUpload — TASK-420
 *
 * Single Responsibility: upload a 30s WebM/Opus audio chunk to the meeting
 * orchestrator's chunk-ingest endpoint and let the backend transcribe it.
 *
 * Pipeline contract (with TASK-421 backend):
 *   1. Frontend's `MediaRecorder` fires `dataavailable` every 30s with a Blob.
 *   2. We POST it as multipart/form-data to `/api/meeting/{sessionId}/chunk`
 *      together with a monotonic `seq` counter.
 *   3. Backend stores the chunk in MinIO, enqueues a transcription job, and
 *      pushes a `chunk_transcribed` WS message back to the cockpit when done.
 *
 * IMPORTANT — WebM container quirk:
 *   `MediaRecorder` with a `timeslice` emits only the first chunk with WebM
 *   codec startup data; subsequent chunks are continuation bytes. The backend
 *   MUST stitch consecutive chunks by `seq` before transcoding via ffmpeg.
 *   The frontend deliberately does not try to fix this — `seq` is the single
 *   source of truth for ordering.
 *
 * Retry semantics:
 *   We use the default `httpClient` (retry enabled). It retries network errors
 *   and 5xx responses with exponential backoff (max 3 attempts) — see
 *   `lib/httpClient.ts:137-146`. The caller still needs an AbortSignal so the
 *   `useMeetingCapture` teardown can cancel in-flight uploads on unmount.
 */

import type { AxiosError } from 'axios'
import { httpClient } from '../../lib/httpClient'
import { MEETING_ENDPOINTS } from '../../config/endpoints'

export interface UploadMeetingChunkParams {
  sessionId: string
  seq: number
  chunk: Blob
  /**
   * Optional AbortSignal. Pass from a useEffect cleanup to cancel uploads
   * that are mid-flight when the recorder unmounts or the user ends the meeting.
   */
  signal?: AbortSignal
  /**
   * Optional mime type override. Defaults to `chunk.type` (whatever the
   * `MediaRecorder` produced). Pass-through for backend logging only.
   */
  mimeType?: string
}

export interface UploadMeetingChunkResult {
  /** Server's assigned chunk id (informational; UI tracks by `seq`). */
  chunkId?: string
  /** Echoed back for sanity. */
  seq: number
  /** Bytes received as confirmed by the server. */
  bytesReceived?: number
}

export class MeetingChunkUploadError extends Error {
  constructor(
    public readonly seq: number,
    public readonly status: number | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'MeetingChunkUploadError'
  }
}

/**
 * Upload a single audio chunk. Throws `MeetingChunkUploadError` on terminal
 * failure (after the httpClient's retry budget is exhausted, or on 4xx).
 */
export async function uploadMeetingChunk(
  params: UploadMeetingChunkParams,
): Promise<UploadMeetingChunkResult> {
  const { sessionId, seq, chunk, signal, mimeType } = params

  if (!sessionId) {
    throw new MeetingChunkUploadError(seq, undefined, 'sessionId is required')
  }
  if (!Number.isInteger(seq) || seq < 0) {
    throw new MeetingChunkUploadError(seq, undefined, 'seq must be a non-negative integer')
  }
  if (!chunk || chunk.size === 0) {
    throw new MeetingChunkUploadError(seq, undefined, 'chunk blob is empty')
  }

  const form = new FormData()
  form.append('chunk', chunk, `chunk-${seq}.webm`)
  form.append('seq', String(seq))
  form.append('session_id', sessionId)
  if (mimeType) {
    form.append('mime_type', mimeType)
  }

  try {
    // The shared `httpClient` defaults `Content-Type: application/json`, which
    // would override axios's multipart auto-detection on `FormData` and make
    // FastAPI parse the body as JSON (→ 422 with our chunk endpoint). Setting
    // the header to `undefined` here forces axios to derive
    // `multipart/form-data; boundary=...` from the FormData payload itself.
    return await httpClient.post<UploadMeetingChunkResult>(
      MEETING_ENDPOINTS.audioChunk(sessionId),
      form,
      {
        signal,
        headers: { 'Content-Type': undefined as unknown as string },
      },
    )
  } catch (err) {
    const axiosErr = err as AxiosError
    if (axiosErr.name === 'CanceledError' || axiosErr.code === 'ERR_CANCELED') {
      // Caller aborted; rethrow so they can no-op without a toast.
      throw err
    }
    const status = axiosErr.response?.status
    const detail = (axiosErr.response?.data as { error?: string } | undefined)?.error
      ?? axiosErr.message
      ?? 'unknown error'
    throw new MeetingChunkUploadError(
      seq,
      status,
      `chunk seq=${seq} upload failed${status ? ` (HTTP ${status})` : ''}: ${detail}`,
    )
  }
}
